import l from "components/util/l";

const CAPTURE_TYPE = "image/png";
// Stands in for an image snapdom could not inline. A file served from the API is
// cross-origin to this app, so a capture containing one used to fail whole: the inlining
// is denied, the serialized SVG comes out corrupt and the browser refuses to decode it.
// A transparent pixel keeps the document valid and costs one blank box.
const BROKEN_IMAGE_FALLBACK =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==";
// A capture that has not produced a canvas by now is not going to: snapdom rasterizes on
// the main thread and a tree it cannot serialize can keep it there indefinitely. The wait
// is bounded so the flow ends in an error the user can read instead of a dead spinner.
const CAPTURE_TIMEOUT = 20000;
const OVERLAY_SELECTOR = "[data-screenshot-helper]";
// Anything a screen marks as not worth capturing. Decorative SVGs are the usual case:
// they add nothing to a screenshot and their internal id references (`url(#id)`,
// `xlink:href="#id"`) are what most often makes the serialized SVG undecodable.
const EXCLUDED_SELECTOR = "[data-screenshot-exclude]";

// snapdom serializes the DOM into an SVG and lets the BROWSER rasterize it, so
// every CSS feature works by construction — modern color functions (oklab, oklch,
// color(), color-mix()), pseudo-elements, container queries. That is why it
// replaced html2canvas here: html2canvas reimplements the render engine and its
// own color parser, frozen since 2022, aborts the whole capture on any color
// syntax it does not know, including inside ::before rules that no amount of
// patching from outside can reach.
//
// It is loaded on demand: nothing needs it until a screenshot is confirmed.
const loadSnapdom = async () => (await import("@zumer/snapdom")).snapdom;

// A CSS container query (`container-type: inline-size`) establishes size containment.
// Inside the standalone SVG snapdom serializes into, a size-contained box has nothing
// definite to resolve its size against, and the document comes out undecodable: the
// capture dies with "The source image cannot be decoded", and the error names no node.
//
// So containment comes off the clone, never the page. snapdom reads each node's computed
// style while the page still has its container queries, which is the layout on screen;
// suspending them on the live document instead re-laid the page for the instant of the
// capture — a narrow card lost the rule that frees its title and came out one letter per
// line.
const CLEAR_CONTAINMENT = {
  name: "clear-containment",
  afterClone: ({ clone }) => {
    for (const node of [clone, ...clone.querySelectorAll("*")])
      node.style.setProperty("container-type", "normal", "important");
  },
};

// What the page's own options leave to every rasterization here: a denied image becomes
// a blank box, and nothing is cached — one denied fetch would otherwise poison every retry,
// and the user's second attempt would fail exactly like the first. Captures are rare
// enough that paying full price each time is right.
const RASTER_OPTIONS = {
  fast: true,
  fallbackURL: BROKEN_IMAGE_FALLBACK,
  cache: "disabled",
  plugins: [CLEAR_CONTAINMENT],
};

const DEFAULT_BACKDROP_COLOR = "#ffffff";

// What sits behind the captured element. A fixed white fill turns a dark UI into a blank
// image — white text on white — so the backdrop is read off the page instead: the first
// ancestor with a fully opaque background is the one actually being seen through. An
// ancestor painting a gradient or an image gets there first when it is closer, and is
// handed back as the `painter` to rasterize, because no flat colour stands in for it.
const getBackdrop = (element) => {

  for (let node = element; node && node.nodeType == 1; node = node.parentElement) {
    const style = getComputedStyle(node);
    if (getAlpha(style.backgroundColor) == 1) return { color: style.backgroundColor, painter: null };
    if (node != element && style.backgroundImage != "none") return { color: DEFAULT_BACKDROP_COLOR, painter: node };
  }

  return { color: DEFAULT_BACKDROP_COLOR, painter: null };

};

// The painter on its own: its background and whatever decoration is layered on it (a
// solution's waves), without the branch that holds the captured content. Rasterizing the
// painter whole means rasterizing everything drawn over it — the whole app — which is the
// capture snapdom does not get through.
const rasterizeBackdrop = (snapdom, painter, element, scale) => {

  const contentBranch = [...painter.children].find((child) => child.contains(element));

  return snapdom.toCanvas(painter, {
    ...RASTER_OPTIONS,
    scale,
    filter: (node) => node != contentBranch,
    filterMode: "remove",
  });

};

const getAlpha = (color) => {

  const channels = /^rgba?\(([^)]+)\)$/.exec(color || "");

  if (!channels) return 0;

  const parts = channels[1].split(",").map((part) => parseFloat(part));
  return parts.length < 4 ? 1 : parts[3];

};

const withTimeout = (promise, timeout, message) => {

  let timer = null;

  const expiration = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error(message);
      // Read by the caller to tell a tree that cannot be serialized from one that is
      // merely too big to finish: retrying a bigger tree after a timeout only doubles
      // the wait.
      error.isTimeout = true;
      reject(error);
    }, timeout);
  });

  return Promise.race([promise, expiration]).finally(() => clearTimeout(timer));

};

// The smallest element that still contains the whole selection.
//
// A free-region drag used to rasterize the entire capture root — the whole app layout —
// only to throw all but the selected rect away. That is the slowest capture possible and
// the one most likely to meet the single node that cannot be serialized, which is what
// made a drag feel frozen where a click on an element was instant. Cutting the same rect
// out of the tightest subtree that contains it keeps the work proportional to what the
// user actually picked.
//
// The walk goes up from what is painted at the middle of the selection, not down from the
// root: going down by document order entered the layout under a modal — a fixed layer
// portaled to the end of the body — and captured what the modal was covering. It also
// stalled on the body itself, which measures 0px tall because everything in it is
// positioned. Whatever backdrop the chosen subtree sits on is repainted by the crop.
const findCaptureTarget = (root, rect) => {

  let target = getPaintedElement(root, rect);

  while (target && target != root && !containsRect(target.getBoundingClientRect(), rect))
    target = target.parentElement;

  return target && root.contains(target) ? target : root;

};

// The topmost element at the selection's center that the capture keeps: neither the
// overlay itself nor anything marked to be left out of it.
const getPaintedElement = (root, rect) =>
  root.ownerDocument
    .elementsFromPoint(rect.left + rect.width / 2, rect.top + rect.height / 2)
    .find((element) => !element.closest(OVERLAY_SELECTOR) && !element.closest(EXCLUDED_SELECTOR));

// A pixel of slack: bounding rects are fractional and a selection clamped to an edge
// lands right on it.
const containsRect = (bounds, rect) =>
  bounds.left <= rect.left + 1 &&
  bounds.top <= rect.top + 1 &&
  bounds.left + bounds.width >= rect.left + rect.width - 1 &&
  bounds.top + bounds.height >= rect.top + rect.height - 1;

// The realm the capture root lives in. It is this window for a host-rendered tree
// and a Module Federation view, and the iframe's window for a legacy HTML view --
// every measurement below has to be taken there.
const getWindow = (root) => root.ownerDocument.defaultView;

// Rasterizes `root` and cuts `rect` out of it.
//
// snapdom has no crop option, so the whole root is rendered and the selection is
// drawn out of it into a second canvas. The scale factor is read back off the
// produced canvas rather than assumed from `scale`, because the library folds the
// device pixel ratio into it.
// `target` is the node actually rasterized; `rect` is still cut in viewport coordinates,
// so a caller can rasterize a small subtree and crop the same selection out of it.
const captureRect = async ({ root, rect, scale, backgroundColor, target }) => {

  const snapdom = await loadSnapdom();
  const sourceNode = target || root;
  // "auto" is the default: whatever the element is really sitting on. An explicit colour
  // still wins, and null still means a transparent PNG.
  const backdrop = backgroundColor == "auto"
    ? getBackdrop(sourceNode)
    : { color: backgroundColor, painter: null };

  const source = await withTimeout(snapdom.toCanvas(sourceNode, {
    ...RASTER_OPTIONS,
    scale,
    // The overlay lives outside the root, but a capture armed over the whole
    // document would otherwise catch its own highlight.
    exclude: [OVERLAY_SELECTOR, EXCLUDED_SELECTOR],
    excludeMode: "remove",
  }), CAPTURE_TIMEOUT, l("La captura tardó demasiado%ES006"));

  const ratio = source.width / sourceNode.getBoundingClientRect().width;

  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(rect.width * ratio));
  canvas.height = Math.max(1, Math.round(rect.height * ratio));

  const ctx = canvas.getContext("2d");

  // The backdrop is painted here rather than passed to snapdom, so the crop owns
  // it whole and a transparent capture never shows through as black.
  if (backdrop.color) {
    ctx.fillStyle = backdrop.color;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  if (backdrop.painter)
    await drawPaintedBackdrop(ctx, snapdom, backdrop.painter, sourceNode, rect, scale);

  drawCrop(ctx, source, sourceNode.getBoundingClientRect(), rect);

  // Reading the canvas throws a SecurityError if anything tainted it, so both
  // reads stay inside the promise the caller is already catching.
  const image = await new Promise((resolve, reject) => {
    try { canvas.toBlob(resolve, CAPTURE_TYPE); }
    catch (error) { reject(error); }
  });

  return { image, dataUrl: canvas.toDataURL(CAPTURE_TYPE) };

};

// A backdrop is scenery: when it cannot be rasterized the capture keeps its flat fill
// rather than failing whole, since the content drawn over it is what was asked for.
const drawPaintedBackdrop = async (ctx, snapdom, painter, element, rect, scale) => {

  try {
    const layer = await withTimeout(rasterizeBackdrop(snapdom, painter, element, scale),
      CAPTURE_TIMEOUT, l("La captura tardó demasiado%ES006"));
    drawCrop(ctx, layer, painter.getBoundingClientRect(), rect);
  } catch (error) {
    console.warn("[ScreenshotHelper] backdrop capture failed", error);
  }

};

// Cuts `rect`, in viewport coordinates, out of `image` — a rasterization of the element
// whose box is `imageRect` — and draws it over the whole canvas. The scale is read back
// off the image because snapdom folds the device pixel ratio into it.
const drawCrop = (ctx, image, imageRect, rect) => {

  const ratio = image.width / imageRect.width;

  ctx.drawImage(
    image,
    (rect.left - imageRect.left) * ratio,
    (rect.top - imageRect.top) * ratio,
    rect.width * ratio,
    rect.height * ratio,
    0,
    0,
    ctx.canvas.width,
    ctx.canvas.height
  );

};

// The bisect below pays one full capture per node, on a page that is already struggling
// to produce one. It is a tool for diagnosing a broken capture, not part of the flow, so
// it stays off until someone turns it on by hand:
//
//   localStorage.setItem("screenshot_debug", "1")
const isCaptureDebugEnabled = () => localStorage.getItem("screenshot_debug") == "1";

// A failed capture says only that the browser could not decode the result, never which
// node produced it. So the failure is turned into a report: each branch is rasterized on
// its own, and the deepest ones that still fail are the ones to blame. Bounded on
// purpose — this runs on a page that just failed, not in a loop.
const findUncapturableNodes = async (root, { maxDepth = 12, maxNodes = 400 } = {}) => {

  const snapdom = await loadSnapdom();
  const culprits = [];
  let tested = 0;

  const canCapture = async (node) => {
    try {
      await snapdom.toCanvas(node, {
        scale: 1,
        fast: true,
        cache: "disabled",
        exclude: [OVERLAY_SELECTOR, EXCLUDED_SELECTOR],
        excludeMode: "remove",
      });
      return true;
    } catch (error) {
      return false;
    }
  };

  // `node` is always one that already failed. Running out of budget still has to name
  // something, or a chain deeper than the limit reports nothing at all.
  const walk = async (node, depth) => {

    if (depth >= maxDepth || tested >= maxNodes) {
      culprits.push(node);
      return;
    }

    // Zero-sized children are tested too: a broken <img> collapses to nothing and is
    // exactly the kind of node that breaks the capture.
    const children = [...node.children];

    let isBlamedOnChild = false;

    for (const child of children) {
      tested++;
      if (await canCapture(child)) continue;
      isBlamedOnChild = true;
      await walk(child, depth + 1);
    }

    // Nothing under it fails on its own, so the node itself is what breaks.
    if (!isBlamedOnChild) culprits.push(node);

  };

  await walk(root, 0);
  console.info(`[ScreenshotHelper] bisect: ${tested} nodos probados, ${culprits.length} culpables`);
  return culprits;

};

// Turns the two corners of a drag into a positive-sized client rect.
const normalizeRect = (startX, startY, endX, endY) => ({
  left: Math.min(startX, endX),
  top: Math.min(startY, endY),
  width: Math.abs(endX - startX),
  height: Math.abs(endY - startY),
});

// A drag can run past the edges of the capture root; the screenshot cannot.
const clampRect = (rect, bounds) => {

  const left = Math.max(rect.left, bounds.left);
  const top = Math.max(rect.top, bounds.top);
  const right = Math.min(rect.left + rect.width, bounds.left + bounds.width);
  const bottom = Math.min(rect.top + rect.height, bounds.top + bounds.height);

  return {
    left,
    top,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
  };

};

export { captureRect, clampRect, findCaptureTarget, findUncapturableNodes, getWindow, isCaptureDebugEnabled, normalizeRect };
